import { type NearbyStop } from "../lib/api";
import { useSite } from "../lib/useSite";
import { MODE_COLORS, MODE_LABELS } from "../map/layers/transit";

/** What you could walk to from the exact spot that was clicked. */
export function NearbyStops({ lon, lat }: { lon: number; lat: number }) {
  const { data, isLoading } = useSite(lon, lat);

  if (isLoading) return <div className="h-16 animate-pulse rounded-lg bg-surface-alt" />;

  const stops = data?.stops ?? [];
  // Rail modes first: a métro entrance five minutes away changes a decision in
  // a way that the eighth nearby bus stop does not.
  const rail = stops.filter((s) => s.mode !== "bus").slice(0, 5);
  const bus = stops.filter((s) => s.mode === "bus");

  if (stops.length === 0) {
    return (
      <p className="text-[11px] text-ink-soft">
        No stops within a 15-minute walk of this spot.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {rail.map((s) => (
        <Row key={`${s.name}-${s.mode}`} stop={s} />
      ))}
      {rail.length === 0 && bus.slice(0, 3).map((s) => (
        <Row key={`${s.name}-${s.mode}`} stop={s} />
      ))}
      {bus.length > 0 && rail.length > 0 && (
        <p className="pt-0.5 text-[11px] text-ink-soft">
          + {bus.length} bus stop{bus.length === 1 ? "" : "s"}, nearest{" "}
          {bus[0].walk_min} min
        </p>
      )}
    </div>
  );
}

function Row({ stop }: { stop: NearbyStop }) {
  return (
    <div className="flex items-baseline gap-2 text-[12px]">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: MODE_COLORS[stop.mode] ?? "#6b7280" }}
      />
      <span className="w-10 shrink-0 tabular-nums text-ink-soft">
        {stop.walk_min} min
      </span>
      <span className="min-w-0 flex-1 truncate">{stop.name}</span>
      <span className="shrink-0 text-[11px] text-ink-soft">
        {MODE_LABELS[stop.mode] ?? stop.mode} {stop.lines}
      </span>
    </div>
  );
}
